import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const TYPES = [
  ["know", "Know / access my information"],
  ["portability", "Get a portable copy"],
  ["delete", "Delete my information"],
  ["correct", "Correct inaccurate information"],
  ["opt_out", "Opt out of sale or sharing"],
  ["limit", "Limit use of sensitive information"],
];

const EMPTY = { full_name: "", email: "", phone: "", report_id: "", details: "", agent_name: "", requester: "self",
  request_types: [], california_resident: false, attest: false, website: "" };

const Field = ({ label, required, children }) => (
  <div className="flex flex-col gap-2">
    <Label className="font-mono-label text-[10px] font-semibold text-oath-secondary">
      {label} {required && <span className="text-oath-accent">*</span>}
    </Label>
    {children}
  </div>
);

const Check = ({ checked, onChange, children, testid }) => (
  <label className="flex cursor-pointer items-start gap-3 text-sm text-oath-secondary">
    <input type="checkbox" checked={checked} onChange={onChange} data-testid={testid} className="mt-0.5 h-4 w-4 accent-oath-ink" />
    <span>{children}</span>
  </label>
);

const Success = ({ result, onReset }) => (
  <div className="text-center" data-testid="privacy-request-success">
    <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-6 w-6" /></span>
    <h3 className="mt-4 font-display text-2xl font-bold text-oath-ink">Request received</h3>
    <p className="mt-2 text-sm text-oath-secondary">
      Your reference number is <strong data-testid="privacy-request-reference">{result.reference}</strong>. We will respond by{" "}
      {new Date(result.due_at).toLocaleDateString(undefined, { dateStyle: "long" })}.
      {result.confirmation_email === "sent" ? " A confirmation has been emailed to you." : " Please save this reference number."}
    </p>
    <button onClick={onReset} className="mt-5 text-sm text-oath-accent underline" data-testid="privacy-request-new">Submit another request</button>
  </div>
);

export const PrivacyRequestForm = ({ source = "california", title = "Submit a privacy rights request" }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const toggleType = (t) => setForm((f) => ({ ...f, request_types: f.request_types.includes(t) ? f.request_types.filter((x) => x !== t) : [...f.request_types, t] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.request_types.length) return toast.error("Choose at least one request type.");
    setLoading(true);
    try {
      const { data } = await api.post("/privacy-requests", { ...form, source });
      setResult(data);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="request-form" className="scroll-mt-28 rounded-3xl border border-gray-200 bg-white p-6 sm:p-10" data-testid="privacy-request-form-card">
      {result ? <Success result={result} onReset={() => setResult(null)} /> : (
        <form onSubmit={submit} data-testid="privacy-request-form">
          <div className="font-mono-label text-[9px] font-semibold text-oath-muted">NO EMAIL NEEDED · FREE · RESPONSE WITHIN 45 DAYS</div>
          <h2 className="mt-2 font-display text-2xl font-bold text-oath-ink">{title}</h2>
          <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true" />
          <div className="mt-6">
            <Label className="font-mono-label text-[10px] font-semibold text-oath-secondary">What would you like to do? <span className="text-oath-accent">*</span></Label>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {TYPES.map(([k, l]) => (
                <button type="button" key={k} onClick={() => toggleType(k)} data-testid={`privacy-type-${k}`}
                  className={`rounded-xl border px-4 py-3 text-left text-sm transition-colors ${form.request_types.includes(k) ? "border-oath-ink bg-oath-ink text-white" : "border-gray-200 text-oath-ink hover:border-gray-400"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <Field label="Full name" required><Input data-testid="privacy-field-name" value={form.full_name} onChange={set("full_name")} required /></Field>
            <Field label="Email" required><Input data-testid="privacy-field-email" type="email" value={form.email} onChange={set("email")} required /></Field>
            <Field label="Phone"><Input data-testid="privacy-field-phone" value={form.phone} onChange={set("phone")} /></Field>
            <Field label="Report or case ID (if you have one)"><Input data-testid="privacy-field-report" value={form.report_id} onChange={set("report_id")} /></Field>
          </div>
          <div className="mt-5">
            <Field label="Details"><Textarea data-testid="privacy-field-details" rows={3} value={form.details} onChange={set("details")} placeholder="Anything that helps us find your information or understand what to correct." /></Field>
          </div>
          <div className="mt-5 flex flex-wrap gap-2" role="radiogroup">
            {[["self", "I am the consumer"], ["agent", "I am an authorized agent"]].map(([k, l]) => (
              <button type="button" key={k} onClick={() => setForm((f) => ({ ...f, requester: k }))} data-testid={`privacy-requester-${k}`}
                className={`rounded-full border px-4 py-2 text-sm ${form.requester === k ? "border-oath-ink bg-oath-ink text-white" : "border-gray-200 text-oath-ink"}`}>{l}</button>
            ))}
          </div>
          {form.requester === "agent" && (
            <div className="mt-4 max-w-sm"><Field label="Agent's name" required><Input data-testid="privacy-field-agent" value={form.agent_name} onChange={set("agent_name")} required /></Field>
              <p className="mt-2 text-xs text-oath-muted">We will ask for the consumer's signed written permission before responding.</p></div>
          )}
          <div className="mt-6 grid gap-3">
            <Check checked={form.california_resident} onChange={set("california_resident")} testid="privacy-check-resident">I am a California resident (or acting for one).</Check>
            <Check checked={form.attest} onChange={set("attest")} testid="privacy-check-attest">I confirm this information is accurate and that I am the person named, or authorized to act for them. <span className="text-oath-accent">*</span></Check>
          </div>
          <button type="submit" disabled={loading} data-testid="privacy-request-submit"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-oath-ink px-6 py-3.5 font-mono-label text-[12px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "SUBMIT REQUEST"}
          </button>
        </form>
      )}
    </section>
  );
};

export default PrivacyRequestForm;
